// src/pages/Login.tsx

import React, { useState } from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  refreshSocketAuth,
} from "../socket";

import "../components/Auth.css";
import SplashScreen from "../components/SplashScreen";

// =====================================================
// TYPES
// =====================================================

interface LoginResponse {
  success: boolean;

  message?: string;

  token?: string;

  user?: {
    id: number | string;
    fullName: string;
    username: string;
    email: string;
    emailVerified: boolean;
    createdAt: string;
  };
}

interface LocationState {
  registered?: boolean;
  returnTo?: string;
  selectedGame?: string;
}

// =====================================================
// API
// =====================================================

import API_URL from "../config/api";

import {
  trackAnalyticsEvent,
} from "../services/analytics";

// =====================================================
// LOGIN
// =====================================================

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ===================================================
  // LOCATION STATE
  // ===================================================

  const locationState =
    (location.state as LocationState | null) ||
    null;

  const registered =
    locationState?.registered === true;

  const returnTo =
    locationState?.returnTo || "/";

  const selectedGame =
    locationState?.selectedGame || null;

  // ===================================================
  // FORM STATE
  // ===================================================

  const [identifier, setIdentifier] =
    useState("");

  const [password, setPassword] =
    useState("");

  // ===================================================
  // UI STATE
  // ===================================================

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    enteringGame,
    setEnteringGame,
  ] = useState<string | null>(
    null
  );
  
  // ===================================================
  // VALIDATION
  // ===================================================

  const isValid =
    identifier.trim() !== "" &&
    password !== "";

  // ===================================================
  // LOGIN
  // ===================================================

  const handleLogin = async (
    event: React.FormEvent
  ) => {
    event.preventDefault();

    setError("");

    // -------------------------------------------------
    // VALIDATE IDENTIFIER
    // -------------------------------------------------

    if (!identifier.trim()) {
      setError(
        "Please enter your username or email."
      );

      return;
    }

    // -------------------------------------------------
    // VALIDATE PASSWORD
    // -------------------------------------------------

    if (!password) {
      setError(
        "Please enter your password."
      );

      return;
    }

    try {
      setLoading(true);

      // ===============================================
      // REQUEST
      // ===============================================

      const response = await fetch(
        `${API_URL}/api/auth/login`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            identifier:
              identifier.trim(),

            password,
          }),
        }
      );

      // ===============================================
      // RESPONSE
      // ===============================================

      const data: LoginResponse =
        await response.json();

      // ===============================================
      // LOGIN FAILED
      // ===============================================

      if (
        !response.ok ||
        !data.success
      ) {
        setError(
          data.message ||
            "Invalid username, email, or password."
        );

        return;
      }

      // ===============================================
      // SECURITY CHECK
      // ===============================================

      if (
        !data.token ||
        !data.user
      ) {
        console.error(
          "Invalid login response:",
          data
        );

        setError(
          "Login succeeded but authentication information was missing."
        );

        return;
      }

      // ===============================================
      // SAVE AUTHENTICATION
      // ===============================================

      localStorage.setItem(
        "token",
        data.token
      );

      localStorage.setItem(
        "user",
        JSON.stringify(data.user)
      );

      console.log(
        `🔐 Logged in as ${data.user.username}`
      );

      // ===============================================
      // ANALYTICS — SUCCESSFUL LOGIN
      // ===============================================
      //
      // The JWT has already been stored above.
      // Therefore the analytics backend can associate
      // this visitor/session with the authenticated user.
      //

      void trackAnalyticsEvent(
        "login",
        {
          pagePath: "/login",
        }
      );

      // ===============================================
      // REFRESH SOCKET AUTHENTICATION
      // ===============================================
      //
      // The socket may have originally connected as
      // a guest before the user logged in.
      //
      // refreshSocketAuth() disconnects that guest
      // socket and reconnects using the JWT that we
      // just stored in localStorage.
      // ===============================================

      refreshSocketAuth();

      console.log(
        `🔄 Socket authentication refreshed for ${data.user.username}`
      );

      // ===============================================
      // REDIRECT / GAME TRANSITION
      // ===============================================

      if (selectedGame) {

        /*
          The player originally selected a game
          before being asked to log in.
        */
        setEnteringGame(
          selectedGame
        );

        /*
          Keep the P-Arena transition visible
          briefly before entering the lobby.
        */
        window.setTimeout(
          () => {

            navigate(
              returnTo,
              {
                replace: true,

                state: {
                  selectedGame,
                },
              }
            );

          },
          1500
        );

        return;
      }


      /*
        Normal login from the Login button.
        No game transition is necessary.
      */
      navigate(
        returnTo,
        {
          replace: true,
        }
      );

    } catch (err) {
      console.error(
        "Login request failed:",
        err
      );

      setError(
        "Unable to connect to the server. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // RETURN
  // ===================================================

  return (
    <div className="app-background auth-container">

      {/* ============================================= */}
      {/* P-ARENA GAME TRANSITION                       */}
      {/* ============================================= */}

      <SplashScreen
        visible={enteringGame !== null}
        text={
          enteringGame
            ? `ENTERING ${enteringGame.toUpperCase()}`
            : "ENTERING THE ARENA"
        }
      />

      {/* ============================================= */}
      {/* BACK TO GAME HOUSE                            */}
      {/* ============================================= */}

      <button
        type="button"
        className="back-home"
        disabled={loading}
        onClick={() =>
          navigate("/")
        }
      >
        ← Game House
      </button>

      {/* ============================================= */}
      {/* LOGIN CARD                                    */}
      {/* ============================================= */}

      <div className="auth-box">

        {/* =========================================== */}
        {/* HEADING                                     */}
        {/* =========================================== */}

        <div className="auth-heading">

          <div className="auth-logo">
            ♠ P-ARENA
          </div>

          <h2>
            Welcome Back
          </h2>

          <p>
            Sign in to play with friends
            and players online.
          </p>

        </div>

        {/* =========================================== */}
        {/* REGISTRATION SUCCESS                        */}
        {/* =========================================== */}

        {registered && (
          <div
            className="auth-success"
            role="status"
          >
            ✓ Account created successfully.
            You can now sign in.
          </div>
        )}

        {/* =========================================== */}
        {/* LOGIN FORM                                  */}
        {/* =========================================== */}

        <form onSubmit={handleLogin}>

          {/* ========================================= */}
          {/* USERNAME / EMAIL                          */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="login-identifier">
              Username or Email
            </label>

            <input
              id="login-identifier"
              type="text"
              placeholder="Enter username or email"
              value={identifier}
              autoComplete="username"
              autoFocus
              disabled={loading}
              onChange={(event) => {
                setIdentifier(
                  event.target.value
                );

                setError("");
              }}
            />

          </div>

          {/* ========================================= */}
          {/* PASSWORD                                  */}
          {/* ========================================= */}

          <div className="input-group">

            <div className="password-label-row">

              <label htmlFor="login-password">
                Password
              </label>

              <button
                type="button"
                className="forgot-password-link"
                disabled={loading}
                onClick={() =>
                  navigate(
                    "/forgot-password"
                  )
                }
              >
                Forgot password?
              </button>

            </div>

            <div className="password-input-wrapper">

              <input
                id="login-password"
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                placeholder="Enter your password"
                value={password}
                autoComplete="current-password"
                disabled={loading}
                onChange={(event) => {
                  setPassword(
                    event.target.value
                  );

                  setError("");
                }}
              />

              {/* SHOW / HIDE PASSWORD */}

              <button
                type="button"
                className="password-toggle"
                disabled={loading}
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
                aria-pressed={
                  showPassword
                }
                onClick={() =>
                  setShowPassword(
                    (current) =>
                      !current
                  )
                }
              >
                {showPassword
                  ? "🙈"
                  : "👁"}
              </button>

            </div>

          </div>

          {/* ========================================= */}
          {/* ERROR                                     */}
          {/* ========================================= */}

          {error && (
            <div
              className="error"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* ========================================= */}
          {/* LOGIN BUTTON                              */}
          {/* ========================================= */}

          <button
            type="submit"
            className="auth-submit-button"
            disabled={
              loading ||
              !isValid
            }
          >

            {loading ? (
              <>
                <span
                  className="button-spinner"
                  aria-hidden="true"
                />

                Signing In...
              </>
            ) : (
              "Login"
            )}

          </button>

        </form>

        {/* =========================================== */}
        {/* REGISTER                                    */}
        {/* =========================================== */}

        <div className="auth-footer">

          <span>
            Don't have an account?
          </span>

          <button
            type="button"
            className="auth-link-button"
            disabled={loading}
            onClick={() =>
              navigate(
                "/register",
                {
                  state: {
                    returnTo,
                    selectedGame:
                      selectedGame || undefined,
                  },
                }
              )
            }
          >
            Create Account
          </button>

        </div>

      </div>

    </div>
  );
};

export default Login;